import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Flame, Heart, Share2, Copy, MessageCircle, Download, Trophy, Footprints,
  Users, CheckCircle2, ArrowRight, Megaphone, DollarSign, Loader2, Zap,
  Camera, CalendarDays, MapPin,
} from 'lucide-react';
import SEO from '../components/SEO';
import { OctoberCommunity } from '../components/OctoberCommunity';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// The challenge has its own frictionless identity: a device token issued at
// pledge time. No Eventism account needed to join.
const ocToken = () => localStorage.getItem('oc_token') || '';
const fmtMoney = (c) => (c / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const DEFAULT_HEROES = ['/october/hero-1.jpg', '/october/hero-2.jpg', '/october/hero-3.jpg', '/october/hero-4.jpg'];

const PLEDGE_OPTIONS = [
  { value: '5k_daily', label: '5K Every Day', desc: 'Run or walk 5 kilometers every day in October' },
  { value: '100_miles_total', label: '100 Miles in October', desc: 'Hit 100 total miles by Halloween' },
  { value: 'custom', label: 'My Own Goal', desc: 'Set your own October movement goal' },
];

const FEED_ICONS = {
  pledge_accepted: Flame, checkin: Footprints, friend_nominated: Megaphone,
  donation: Heart, milestone: Trophy, announcement: Zap,
};

const loadImg = (src) => new Promise((resolve, reject) => {
  const im = new Image();
  im.crossOrigin = 'anonymous';
  im.onload = () => resolve(im);
  im.onerror = reject;
  im.src = src;
});

// Draws a 1080x1920 IG-story challenger card (with photo if available) and downloads as PNG.
async function downloadShareCard(name, goalLabel, refLink, avatarUrl) {
  const c = document.createElement('canvas');
  c.width = 1080; c.height = 1920;
  const x = c.getContext('2d');
  const g = x.createLinearGradient(0, 0, 0, 1920);
  g.addColorStop(0, '#1e1b4b'); g.addColorStop(0.55, '#312e81'); g.addColorStop(1, '#0f0a2e');
  x.fillStyle = g; x.fillRect(0, 0, 1080, 1920);
  x.fillStyle = 'rgba(251,146,60,0.18)';
  x.beginPath(); x.arc(900, 260, 340, 0, Math.PI * 2); x.fill();
  x.fillStyle = 'rgba(217,70,239,0.15)';
  x.beginPath(); x.arc(140, 1650, 300, 0, Math.PI * 2); x.fill();
  let avatar = null;
  if (avatarUrl) { try { avatar = await loadImg(avatarUrl); } catch { /* text-only card */ } }
  x.textAlign = 'center';
  x.fillStyle = '#fbbf24';
  x.font = 'bold 44px Arial';
  x.fillText('EVENTISM PRESENTS', 540, 340);
  x.fillStyle = '#ffffff';
  x.font = 'bold 130px Arial';
  x.fillText('OCTOBER', 540, 490);
  x.fillText('CHALLENGE', 540, 625);
  x.fillStyle = '#e0e7ff';
  x.font = '48px Arial';
  x.fillText('I accepted the challenge!', 540, 740);
  if (avatar) {
    const cx = 540, cy = 950, r = 150;
    x.save();
    x.beginPath(); x.arc(cx, cy, r, 0, Math.PI * 2); x.clip();
    const s = Math.max((r * 2) / avatar.width, (r * 2) / avatar.height);
    x.drawImage(avatar, cx - (avatar.width * s) / 2, cy - (avatar.height * s) / 2, avatar.width * s, avatar.height * s);
    x.restore();
    x.strokeStyle = '#fbbf24'; x.lineWidth = 10;
    x.beginPath(); x.arc(cx, cy, r, 0, Math.PI * 2); x.stroke();
  }
  const yName = avatar ? 1220 : 920;
  x.fillStyle = '#ffffff';
  x.font = 'bold 84px Arial';
  x.fillText(name.slice(0, 20), 540, yName);
  x.fillStyle = '#a5b4fc';
  x.font = 'bold 52px Arial';
  x.fillText(goalLabel, 540, yName + 95);
  x.fillStyle = '#f0abfc';
  x.font = '44px Arial';
  x.fillText('for Black health equity in Colorado', 540, yName + 185);
  x.fillStyle = '#ffffff';
  x.font = 'bold 56px Arial';
  x.fillText("WHO'S NEXT? 🔥", 540, avatar ? 1540 : 1360);
  x.fillStyle = '#fbbf24';
  x.font = '40px Arial';
  x.fillText('eventism.com/october-challenge', 540, avatar ? 1640 : 1480);
  x.fillStyle = '#c7d2fe';
  x.font = '34px Arial';
  x.fillText('#OctoberChallenge  #RunForEquity  #Eventism', 540, avatar ? 1720 : 1580);
  const a = document.createElement('a');
  a.download = 'october-challenge-card.png';
  a.href = c.toDataURL('image/png');
  a.click();
}

const goalLabelOf = (t) => ({ '5k_daily': '5K EVERY DAY', '100_miles_total': '100 MILES IN OCTOBER', custom: 'MY OWN OCTOBER GOAL' }[t] || '5K EVERY DAY');

function ShareRow({ refLink, shareText, onShared }) {
  const track = (channel) => { axios.post(`${API}/october/share`, { channel }).catch(() => {}); onShared?.(); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(refLink); toast.success('Link copied!'); track('copy'); }
    catch { toast.error('Could not copy'); }
  };
  const native = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'October Challenge by Eventism', text: shareText, url: refLink }); track('native'); } catch { /* cancelled */ }
    } else copy();
  };
  const enc = encodeURIComponent(`${shareText} ${refLink}`);
  return (
    <div className="flex flex-wrap gap-2" data-testid="share-buttons-row">
      <Button size="sm" variant="outline" className="rounded-full" onClick={copy} data-testid="share-copy-btn"><Copy className="h-3.5 w-3.5 mr-1.5" />Copy link</Button>
      <Button size="sm" variant="outline" className="rounded-full" asChild>
        <a href={`sms:?&body=${enc}`} onClick={() => track('sms')} data-testid="share-sms-btn"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />SMS</a>
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" asChild>
        <a href={`https://wa.me/?text=${enc}`} target="_blank" rel="noreferrer" onClick={() => track('whatsapp')} data-testid="share-whatsapp-btn">WhatsApp</a>
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" asChild>
        <a href={`https://twitter.com/intent/tweet?text=${enc}`} target="_blank" rel="noreferrer" onClick={() => track('twitter')} data-testid="share-x-btn">X / Twitter</a>
      </Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={native} data-testid="share-native-btn"><Share2 className="h-3.5 w-3.5 mr-1.5" />More</Button>
    </div>
  );
}

function NominateForm({ onDone }) {
  const [names, setNames] = useState(['', '', '']);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const friends = names.map((n) => n.trim()).filter(Boolean);
    if (!friends.length) { toast.error('Add at least one friend'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/october/nominate`, { friends, token: ocToken() });
      toast.success(`Challenge sent to ${friends.length} friend${friends.length > 1 ? 's' : ''}! 🔥`);
      setNames(['', '', '']);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not nominate');
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="nominate-form">
      <p className="text-sm font-heading font-semibold">Nominate 3 friends — call them out publicly</p>
      {names.map((n, i) => (
        <Input key={i} value={n} placeholder={`Friend ${i + 1} name`} className="h-9"
          onChange={(e) => setNames(names.map((x, j) => (j === i ? e.target.value : x)))}
          data-testid={`nominate-friend-${i}`} />
      ))}
      <Button size="sm" onClick={submit} disabled={busy} className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white hover:text-white" data-testid="nominate-submit-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Megaphone className="h-4 w-4 mr-1.5" />Challenge them</>}
      </Button>
    </div>
  );
}

function ChallengerWall({ wall, count, onJoin }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12" data-testid="oc-wall-section">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-display font-bold">The Challenger Wall</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {count > 0 ? `${count} challenger${count === 1 ? '' : 's'} in — these are the faces of October.` : 'The faces of October — join and get yours up here.'}
          </p>
        </div>
        <Button size="sm" onClick={onJoin} className="rounded-full bg-orange-500 hover:bg-orange-400 text-white hover:text-white" data-testid="wall-join-btn">
          <Camera className="h-4 w-4 mr-1.5" />Get on the wall
        </Button>
      </div>
      {wall.length === 0 ? (
        <button onClick={onJoin} className="w-full rounded-2xl border-2 border-dashed border-border py-12 text-center hover:bg-accent/50 transition-colors" data-testid="wall-empty-cta">
          <Camera className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="font-heading font-semibold">Be the first face on the wall</p>
          <p className="text-sm text-muted-foreground">Accept the challenge and add your photo — it takes 10 seconds.</p>
        </button>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2" data-testid="wall-grid">
          {wall.map((w, i) => (
            <div key={w.user_name + i} className="group relative aspect-square overflow-hidden rounded-xl bg-muted" data-testid="wall-photo">
              <img src={w.avatar} alt={w.user_name} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-2 pt-7 pb-1.5">
                <p className="text-[11px] font-heading font-semibold text-white truncate">{w.user_name}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PhotoPicker({ preview, onPick, label = 'Add a photo (optional)' }) {
  return (
    <label className="flex items-center gap-3 rounded-xl border border-dashed border-border px-4 py-3 cursor-pointer hover:bg-accent/50 transition-colors" data-testid="photo-picker">
      <input type="file" accept="image/*" className="hidden" data-testid="pledge-photo-input"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }} />
      {preview ? (
        <img src={preview} alt="preview" className="h-12 w-12 rounded-full object-cover ring-2 ring-orange-400" />
      ) : (
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500/10 text-orange-400"><Camera className="h-5 w-5" /></span>
      )}
      <div className="min-w-0">
        <p className="text-sm font-heading font-semibold">{preview ? 'Looking good! Tap to change' : label}</p>
        <p className="text-xs text-muted-foreground">Get featured on the Challenger Wall</p>
      </div>
    </label>
  );
}

export default function OctoberChallengePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [feed, setFeed] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [wall, setWall] = useState([]);
  const [me, setMe] = useState(null);
  const [nextRun, setNextRun] = useState(null);
  const [runCount, setRunCount] = useState(0);
  const [heroImgs, setHeroImgs] = useState(DEFAULT_HEROES);
  const [heroIdx, setHeroIdx] = useState(0);
  const [pledgeOpen, setPledgeOpen] = useState(false);
  const [celebrateOpen, setCelebrateOpen] = useState(false);
  const [pledgeType, setPledgeType] = useState('5k_daily');
  const [pledgeName, setPledgeName] = useState('');
  const [pledgeEmail, setPledgeEmail] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [busy, setBusy] = useState(false);
  const [km, setKm] = useState('5');
  const [note, setNote] = useState('');
  const [donateAmt, setDonateAmt] = useState(50);
  const [customAmt, setCustomAmt] = useState('');
  const [donorName, setDonorName] = useState('');
  const donationChecked = useRef(false);
  const laterPhotoRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/october/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/october/feed?limit=25`).then((r) => setFeed(r.data.posts)).catch(() => {});
    axios.get(`${API}/october/leaderboard`).then((r) => setLeaders(r.data.leaders)).catch(() => {});
    axios.get(`${API}/october/wall`).then((r) => setWall(r.data.wall)).catch(() => {});
    axios.get(`${API}/october/events`).then((r) => {
      const evs = r.data.events || [];
      setRunCount(evs.length);
      const today = new Date().toISOString().slice(0, 10);
      setNextRun(evs.find((e) => e.date >= today) || evs[evs.length - 1] || null);
    }).catch(() => {});
    if (ocToken()) {
      axios.get(`${API}/october/me`, { params: { token: ocToken() } }).then((r) => setMe(r.data)).catch(() => {});
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Hero slideshow: admin-uploaded real photos lead, AI park-run shots follow
  useEffect(() => {
    axios.get(`${API}/october/hero-images`).then((r) => {
      const custom = (r.data.images || []).map((i) => i.url);
      if (custom.length) setHeroImgs([...custom, ...DEFAULT_HEROES].slice(0, 8));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (heroImgs.length < 2) return undefined;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroImgs.length), 5000);
    return () => clearInterval(t);
  }, [heroImgs.length]);

  // Capture ?ref= referral code for credit at pledge time
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) localStorage.setItem('oc_ref', ref);
  }, [searchParams]);

  // Donation return: verify payment
  useEffect(() => {
    const did = searchParams.get('donation_id');
    if (!did || donationChecked.current) return;
    donationChecked.current = true;
    axios.get(`${API}/october/donations/${did}/status`).then((r) => {
      if (r.data.status === 'completed') {
        toast.success(`Thank you! Your ${fmtMoney(r.data.amount_cents)} donation is in. 💜`);
        load();
      } else {
        toast.message('Donation not completed — you can try again anytime.');
      }
      searchParams.delete('donation_id');
      setSearchParams(searchParams, { replace: true });
    }).catch(() => {});
  }, [searchParams, setSearchParams, load]);

  const acceptChallenge = () => {
    // Zero friction — no account needed. Name (+ optional email/photo) is enough.
    if (me?.pledge) { setCelebrateOpen(true); return; }
    setPledgeOpen(true);
  };

  const pickPhoto = (f) => {
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const uploadPhoto = async (file, token) => {
    const fd = new FormData();
    fd.append('token', token);
    fd.append('file', file);
    const r = await axios.post(`${API}/october/photo`, fd);
    return r.data.avatar;
  };

  const submitPledge = async () => {
    if (!pledgeName.trim()) { toast.error('Tell us your first name to join!'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/october/pledge`, {
        name: pledgeName.trim(),
        email: pledgeEmail.trim() || null,
        pledge_type: pledgeType,
        referred_by: localStorage.getItem('oc_ref') || null,
      });
      localStorage.setItem('oc_token', r.data.challenger_token);
      localStorage.removeItem('oc_ref');
      let pledge = r.data.pledge;
      if (photoFile) {
        try {
          const avatar = await uploadPhoto(photoFile, r.data.challenger_token);
          pledge = { ...pledge, avatar };
        } catch { toast.message('Photo upload failed — you can add it from your challenger card.'); }
      }
      setMe({ pledge, checked_in_today: false });
      setPledgeOpen(false);
      setCelebrateOpen(true);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save your pledge');
    } finally { setBusy(false); }
  };

  const addPhotoLater = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const avatar = await uploadPhoto(f, ocToken());
      setMe((m) => ({ ...m, pledge: { ...m.pledge, avatar } }));
      toast.success("You're on the wall! 📸");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Photo upload failed');
    } finally { setBusy(false); }
  };

  const checkin = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/october/checkin`, { distance_km: km, note, token: ocToken() });
      toast.success('Checked in — keep the streak alive! 🔥');
      setNote('');
      setMe((m) => ({ ...m, checked_in_today: true }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Check-in failed');
    } finally { setBusy(false); }
  };

  const donate = async () => {
    const amount = customAmt ? parseFloat(customAmt) : donateAmt;
    if (!amount || amount < 1) { toast.error('Enter a valid amount'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/october/donate`, {
        amount, donor_name: donorName || (me?.pledge?.user_name ?? ''), is_anonymous: !donorName && !me?.pledge?.user_name,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start donation');
      setBusy(false);
    }
  };

  const pct = stats ? Math.min(100, (stats.raised_cents / stats.goal_cents) * 100) : 0;
  const refLink = me?.pledge ? `https://eventism.com/october-challenge?ref=${me.pledge.referral_code}` : 'https://eventism.com/october-challenge';
  const shareText = "I just took the October Challenge by Eventism — 5K every day around Denver's parks! I'm challenging you to do it with me:";

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="october-challenge-page">
      <SEO
        title="October Challenge by Eventism — 5K Every Day, Together in Denver"
        description="Join the October Challenge: 5K every day or 100 miles in October. Kickoff runs at Sloan's Lake and parks across Denver, a live community feed, and the Challenger Wall. Every mile supports Black health equity in Colorado."
        canonical="https://eventism.com/october-challenge"
      />

      {/* Hero — photo slideshow of park runs */}
      <section className="relative overflow-hidden bg-slate-950 text-white" data-testid="oc-hero">
        <div className="absolute inset-0" data-testid="oc-hero-slideshow">
          {heroImgs.map((src, i) => (
            <div key={src} aria-hidden="true"
              className="absolute inset-0 bg-cover bg-center transition-opacity duration-[1800ms] ease-in-out"
              style={{ backgroundImage: `url(${src})`, opacity: i === heroIdx ? 1 : 0 }} />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/35" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/70 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 space-y-6">
            <Badge className="bg-orange-500/20 text-orange-200 border-orange-400/40 uppercase tracking-[0.15em] text-[11px] backdrop-blur">
              Eventism presents · Oct 1–31 · Denver
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] drop-shadow-lg">
              The <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-300 to-orange-400">October Challenge</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-100 max-w-xl leading-relaxed drop-shadow">
              Move every day this October — <strong className="text-white">5K around Sloan's Lake</strong>, your neighborhood park, anywhere. Run with the community, log your miles, and get your face on the Challenger Wall.
            </p>
            {nextRun && (
              <a href="#kickoff-runs" className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-4 py-2 text-sm hover:bg-white/20 transition-colors" data-testid="hero-next-run-chip">
                <CalendarDays className="h-4 w-4 text-orange-300" />
                <span className="font-heading font-semibold">{nextRun.name}</span>
                <span className="text-slate-300 hidden sm:inline">· {new Date(`${nextRun.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                <MapPin className="h-3.5 w-3.5 text-slate-300" />
                <span className="text-slate-300 truncate max-w-[160px]">{nextRun.location}</span>
              </a>
            )}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={acceptChallenge} className="rounded-full btn-glow bg-orange-500 hover:bg-orange-400 text-white hover:text-white text-base px-8" data-testid="accept-challenge-btn">
                {me?.pledge ? 'View My Challenger Card' : 'Accept the Challenge'} <Flame className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full border-white/30 bg-white/5 backdrop-blur text-white hover:bg-white/15 hover:text-white text-base px-8" asChild>
                <a href="#kickoff-runs" data-testid="hero-kickoff-btn"><CalendarDays className="mr-2 h-4 w-4" />Kickoff Runs</a>
              </Button>
            </div>
            <p className="text-sm text-slate-300">
              Every mile supports Black health equity in Colorado. <a href="#donate" className="underline underline-offset-2 text-fuchsia-300 hover:text-fuchsia-200" data-testid="hero-donate-link">Chip in →</a>
            </p>
          </div>
          <div className="lg:col-span-5">
            <Card className="border-white/10 bg-slate-950/60 backdrop-blur-xl" data-testid="oc-progress-card">
              <CardContent className="p-6 space-y-5">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div><p className="text-2xl font-heading font-bold text-white" data-testid="oc-challengers">{stats ? stats.challenger_count : '—'}</p><p className="text-[11px] text-slate-400 uppercase tracking-wider">challengers</p></div>
                  <div><p className="text-2xl font-heading font-bold text-white" data-testid="oc-km">{stats ? stats.total_km.toLocaleString() : '—'}</p><p className="text-[11px] text-slate-400 uppercase tracking-wider">km logged</p></div>
                  <div><p className="text-2xl font-heading font-bold text-white" data-testid="oc-runs">{runCount}</p><p className="text-[11px] text-slate-400 uppercase tracking-wider">group runs</p></div>
                </div>
                <div className="pt-1 border-t border-white/10">
                  <div className="flex items-end justify-between mb-1.5 pt-3">
                    <p className="text-sm text-slate-300"><span className="font-heading font-bold text-white" data-testid="oc-raised">{stats ? fmtMoney(stats.raised_cents) : '—'}</span> raised for Black health equity</p>
                    <p className="text-xs text-slate-400">of $50k</p>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-orange-400 via-fuchsia-400 to-indigo-400 transition-all duration-700" style={{ width: `${Math.max(pct, 1.5)}%` }} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Challenger Wall — the faces of October */}
      <ChallengerWall wall={wall} count={stats?.challenger_count || 0}
        onJoin={() => { if (me?.pledge) setCelebrateOpen(true); else setPledgeOpen(true); }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-8">
        {/* Left: check-in + feed */}
        <div className="lg:col-span-2 space-y-6">
          {me?.pledge && (
            <Card className="border-orange-500/30 bg-orange-500/5" data-testid="oc-checkin-card">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="font-heading font-semibold flex items-center gap-2"><Footprints className="h-4 w-4 text-orange-400" />Daily check-in</p>
                  <p className="text-xs text-muted-foreground">{me.pledge.total_distance_km?.toFixed(1)} km · {me.pledge.checkin_count} check-ins · {me.pledge.referral_count || 0} referrals</p>
                </div>
                {me.checked_in_today ? (
                  <p className="text-sm text-emerald-500 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Checked in today — see you tomorrow!</p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input type="number" min={0.1} step={0.1} value={km} onChange={(e) => setKm(e.target.value)} className="w-24 h-9" data-testid="checkin-km-input" />
                    <span className="text-sm text-muted-foreground">km</span>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="How'd it go? (optional)" className="flex-1 min-w-[160px] h-9" data-testid="checkin-note-input" />
                    <Button size="sm" onClick={checkin} disabled={busy} className="rounded-full bg-orange-500 hover:bg-orange-400 text-white hover:text-white" data-testid="checkin-submit-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Log it'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="space-y-3" data-testid="oc-feed">
            <h2 className="text-lg font-heading font-bold flex items-center gap-2"><Zap className="h-5 w-5 text-fuchsia-400" />Live activity</h2>
            {feed.length === 0 && <p className="text-sm text-muted-foreground">Be the first — accept the challenge and kick off the feed!</p>}
            {feed.map((p) => {
              const Icon = FEED_ICONS[p.type] || Zap;
              return (
                <div key={p.post_id} className="flex items-start gap-3 rounded-xl border border-border bg-card/60 px-4 py-3" data-testid="oc-feed-item">
                  {p.author_avatar ? (
                    <img src={p.author_avatar} alt={p.author_name} className="mt-0.5 h-8 w-8 shrink-0 rounded-full object-cover" data-testid="feed-avatar" />
                  ) : (
                    <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${p.type === 'donation' ? 'bg-fuchsia-500/15 text-fuchsia-400' : p.type === 'pledge_accepted' ? 'bg-orange-500/15 text-orange-400' : 'bg-indigo-500/15 text-indigo-400'}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm leading-snug">{p.content}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(p.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: leaderboard + share + donate */}
        <div className="space-y-6">
          <Card data-testid="oc-leaderboard">
            <CardContent className="p-5 space-y-3">
              <p className="font-heading font-semibold flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-400" />Leaderboard</p>
              {leaders.length === 0 && <p className="text-sm text-muted-foreground">No miles logged yet — yours could be first.</p>}
              {leaders.map((l, i) => (
                <div key={l.user_name + i} className="flex items-center gap-3 text-sm">
                  <span className={`w-6 text-center font-heading font-bold ${i < 3 ? 'text-amber-400' : 'text-muted-foreground'}`}>{i + 1}</span>
                  {l.avatar ? <img src={l.avatar} alt="" className="h-6 w-6 rounded-full object-cover" /> : null}
                  <span className="flex-1 truncate">{l.user_name}</span>
                  <span className="font-heading font-bold">{(l.total_distance_km || 0).toFixed(1)} km</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {me?.pledge && !celebrateOpen && (
            <Card className="border-indigo-500/30" data-testid="oc-share-card-panel">
              <CardContent className="p-5 space-y-3">
                <p className="font-heading font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-indigo-400" />Grow the movement</p>
                <ShareRow refLink={refLink} shareText={shareText} onShared={load} />
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadShareCard(me.pledge.user_name, goalLabelOf(me.pledge.pledge_type), refLink, me.pledge.avatar)} data-testid="download-story-card-btn">
                  <Download className="h-3.5 w-3.5 mr-1.5" />IG Story card
                </Button>
                <NominateForm onDone={load} />
              </CardContent>
            </Card>
          )}

          <Card id="donate" className="border-fuchsia-500/30 scroll-mt-24" data-testid="oc-donate-card">
            <CardContent className="p-5 space-y-4">
              <p className="font-heading font-semibold flex items-center gap-2"><Heart className="h-4 w-4 text-fuchsia-400" />Fuel the mission</p>
              <div className="grid grid-cols-4 gap-2">
                {[25, 50, 100, 250].map((a) => (
                  <button key={a} onClick={() => { setDonateAmt(a); setCustomAmt(''); }}
                    className={`rounded-lg border px-2 py-2 text-sm font-heading font-bold transition-colors ${donateAmt === a && !customAmt ? 'border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-400' : 'border-border hover:bg-accent'}`}
                    data-testid={`donate-chip-${a}`}>${a}</button>
                ))}
              </div>
              <Input type="number" min={1} placeholder="Custom amount ($)" value={customAmt} onChange={(e) => setCustomAmt(e.target.value)} data-testid="donate-custom-input" />
              <Input placeholder="Your name (leave blank = anonymous)" value={donorName} onChange={(e) => setDonorName(e.target.value)} data-testid="donate-name-input" />
              <Button onClick={donate} disabled={busy} className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-500 text-white hover:text-white" data-testid="donate-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><DollarSign className="h-4 w-4 mr-1" />Donate {customAmt ? `$${customAmt}` : `$${donateAmt}`}</>}
              </Button>
              <p className="text-[11px] text-muted-foreground">Secure checkout via Stripe. Every dollar supports Black health equity programs in Colorado.</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Community: kickoff runs, captains, partners, sponsors */}
      <OctoberCommunity hasPledge={!!me?.pledge} onNeedPledge={() => setPledgeOpen(true)} />

      {/* Pledge modal */}
      <Dialog open={pledgeOpen} onOpenChange={setPledgeOpen}>
        <DialogContent className="max-w-md" data-testid="pledge-modal">
          <DialogHeader><DialogTitle className="font-heading">Join in 10 seconds — it's free</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Input value={pledgeName} onChange={(e) => setPledgeName(e.target.value)} placeholder="Your first name *" data-testid="pledge-name-input" />
            <Input type="email" value={pledgeEmail} onChange={(e) => setPledgeEmail(e.target.value)} placeholder="Email (optional — for updates)" data-testid="pledge-email-input" />
            <PhotoPicker preview={photoPreview} onPick={pickPhoto} />
          </div>
          <div className="space-y-2">
            {PLEDGE_OPTIONS.map((o) => (
              <button key={o.value} onClick={() => setPledgeType(o.value)}
                className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${pledgeType === o.value ? 'border-orange-500 bg-orange-500/10' : 'border-border hover:bg-accent'}`}
                data-testid={`pledge-option-${o.value}`}>
                <p className="font-heading font-semibold text-sm">{o.label}</p>
                <p className="text-xs text-muted-foreground">{o.desc}</p>
              </button>
            ))}
          </div>
          <Button onClick={submitPledge} disabled={busy} className="w-full rounded-full bg-orange-500 hover:bg-orange-400 text-white hover:text-white" data-testid="pledge-confirm-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>I'm in — Accept the Challenge <ArrowRight className="ml-1.5 h-4 w-4" /></>}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Celebration modal */}
      <Dialog open={celebrateOpen} onOpenChange={setCelebrateOpen}>
        <DialogContent className="max-w-md" data-testid="celebration-modal">
          <DialogHeader><DialogTitle className="font-heading">🎉 Challenge Accepted!</DialogTitle></DialogHeader>
          {me?.pledge && (
            <div className="space-y-4">
              <div className="rounded-2xl bg-gradient-to-br from-indigo-950 via-indigo-900 to-slate-950 p-6 text-center text-white space-y-2" data-testid="challenger-badge-card">
                <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">Eventism presents</p>
                <p className="text-2xl font-display font-bold">OCTOBER CHALLENGE</p>
                {me.pledge.avatar && (
                  <img src={me.pledge.avatar} alt={me.pledge.user_name} className="mx-auto h-20 w-20 rounded-full object-cover ring-4 ring-amber-300/70" data-testid="badge-avatar" />
                )}
                <p className="text-sm text-indigo-200">Official Challenger</p>
                <p className="text-xl font-heading font-bold">{me.pledge.user_name}</p>
                <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40">{goalLabelOf(me.pledge.pledge_type)}</Badge>
                <p className="text-xs text-fuchsia-200">for Black health equity in Colorado</p>
                <p className="text-[11px] font-mono text-indigo-300 break-all">{refLink}</p>
              </div>
              {!me.pledge.avatar && (
                <>
                  <input ref={laterPhotoRef} type="file" accept="image/*" className="hidden" data-testid="later-photo-input"
                    onChange={(e) => addPhotoLater(e.target.files?.[0])} />
                  <Button size="sm" variant="outline" className="rounded-full w-full border-orange-500/40 text-orange-500" disabled={busy}
                    onClick={() => laterPhotoRef.current?.click()} data-testid="add-photo-later-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Camera className="h-3.5 w-3.5 mr-1.5" />Add your photo to the Challenger Wall</>}
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" className="rounded-full w-full" onClick={() => downloadShareCard(me.pledge.user_name, goalLabelOf(me.pledge.pledge_type), refLink, me.pledge.avatar)} data-testid="celebration-download-btn">
                <Download className="h-3.5 w-3.5 mr-1.5" />Download IG Story card
              </Button>
              <ShareRow refLink={refLink} shareText={shareText} onShared={load} />
              <NominateForm onDone={() => { setCelebrateOpen(false); load(); }} />
              <p className="text-[11px] text-muted-foreground">
                Optional: <Link to="/login?returnTo=/october-challenge" className="underline text-indigo-400">create a free Eventism account</Link> later to sync your progress across devices and post in community areas.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
